import { Helmet } from "react-helmet";
import {
  Button,
  Dropdown,
  EmptyState,
  Flex,
  SimpleTable,
  Tag,
  Typography,
} from "djuno-design";
import useServiceType from "../../hooks/useServiceType";
import QuestionModal, {
  DeleteModal,
} from "../../components/modals/QuestionModal";

import RefreshIcon from "./../../assets/icons/arrow-path.svg?react";
import PlusIcon from "./../../assets/icons/plus.svg?react";
import DetailsIcon from "./../../assets/icons/bars-3-bottom-left.svg?react";
import ArchiveIcon from "./../../assets/icons/archive-box.svg?react";
import MoreIcon from "./../../assets/icons/more.svg?react";
import ArrowRightIcon from "./../../assets/icons/arrow-right.svg?react";
import StartIcon from "./../../assets/icons/play.svg?react";
import StopIcon from "./../../assets/icons/stop.svg?react";

import { useNavigate } from "react-router-dom";
import {
  AiNotebooksCreateUrl,
  AiNotebooksGeneralInformationUrl,
} from "../../utils/urls";
import HighlighterText from "../../components/general/HighlighterText";
import { useSearch } from "../../providers/SearchProvider";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useCallback, useEffect, useState } from "react";
import { AiNotebook, AiNotebookStatus } from "../../types/ai";
import {
  AiNotebooksRefreshStatus,
  getAiNotebooksAsync,
  selectAiNotebooks,
  selectAiNotebooksActionLoading,
  selectAiNotebooksLoading,
  deleteAiNotebookAsync,
  stopAiNotebookAsync,
  startAiNotebookAsync,
} from "../../store/ai/notebooks/notebooksSlice";
import { formatDuration } from "../../utils/date";

const AiNotebooksPage = () => {
  const { serviceType } = useServiceType(
    import.meta.env.VITE_AI_NOTEBOOKS_SERVICE_TYPE
  );
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { value: searchValue } = useSearch();

  const [deleteService, setDeleteService] = useState<AiNotebook | null>(null);

  const notebooks = useAppSelector(selectAiNotebooks);
  const notebooksLoading = useAppSelector(selectAiNotebooksLoading);

  const [filteredNotebooks, setFilteredNotebooks] = useState<AiNotebook[]>([]);

  const notebooksActionLoading = useAppSelector(selectAiNotebooksActionLoading);

  useEffect(() => {
    dispatch(getAiNotebooksAsync({}));
  }, [dispatch]);

  useEffect(() => {
    const lookedUpFiles = notebooks?.filter((service) =>
      service.spec.name.toLowerCase().includes(searchValue.toLowerCase())
    );
    setFilteredNotebooks(lookedUpFiles);
  }, [dispatch, searchValue, notebooks]);

  //refresh the list
  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    const intervalTime =
      Number(import.meta.env.VITE_NODES_REFRESH_INTERVAL) || 20000;

    const shouldSetInterval = notebooks.some((notebook) =>
      AiNotebooksRefreshStatus.includes(notebook.status.state || "")
    );

    if (shouldSetInterval) {
      interval = setInterval(() => {
        dispatch(getAiNotebooksAsync({ withoutLoading: true }));
      }, intervalTime);
    } else {
      if (interval) clearInterval(interval);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [dispatch, notebooks]);

  const [stoppingNotebook, setStoppingNotebook] = useState<null | string>(null);
  const handleStop = useCallback(
    (id: string) => {
      dispatch(stopAiNotebookAsync({ id })).then((action) => {
        if (action.type === "ai-notebooks/stop/fulfilled") {
          dispatch(getAiNotebooksAsync({ withoutLoading: true }));
          setStoppingNotebook(null);
        }
      });
    },
    [dispatch]
  );

  const handleStart = useCallback(
    (id: string) => {
      dispatch(startAiNotebookAsync({ id })).then((action) => {
        if (action.type === "ai-notebooks/start/fulfilled") {
          dispatch(getAiNotebooksAsync({ withoutLoading: true }));
        }
      });
    },
    [dispatch]
  );

  return (
    <>
      <Helmet>
        <title>
          {import.meta.env.VITE_APP_NAME} | {serviceType?.ServiceName || ""}
        </title>
        <meta name="description" content="" />
      </Helmet>
      <div className="flex items-center justify-between h-20">
        <div className="px-6 items-center justify-between flex flex-1 transition duration-150">
          <Typography.Title level={5} className="!mb-0">
            {serviceType?.ServiceName}
          </Typography.Title>
        </div>
        {/* <div className="mr-6 w-96"></div> */}
      </div>
      <div className="mx-6">
        <div className="py-6">
          <div className="">
            <div className="flex items-center justify-between databases-header">
              <div className="items-center justify-between flex flex-1 transition duration-150">
                <Typography.Text className="font-medium text-lg"></Typography.Text>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => dispatch(getAiNotebooksAsync({}))}
                  className="refresh-button group"
                  tooltip={{ content: "Refresh" }}
                >
                  <RefreshIcon className="w-4 h-4 group-hover:rotate-90 group-hover:scale-110 transition-all duration-500" />
                </Button>
                <Button
                  uiType="primary"
                  onClick={() => navigate(AiNotebooksCreateUrl)}
                  className="create-database-button group"
                >
                  Create a Notebook
                  <PlusIcon className="w-3 h-3 group-hover:rotate-90 group-hover:scale-110 transition-all duration-500" />
                </Button>
              </div>
            </div>

            <div className="mt-5 w-full database-table">
              <SimpleTable
                loading={notebooksLoading}
                containerClassName="min-h-[240px]"
              >
                <SimpleTable.Head>
                  <SimpleTable.Row>
                    <SimpleTable.TH lable="Name" />
                    <SimpleTable.TH lable="Region" />
                    <SimpleTable.TH lable="Environment" />
                    <SimpleTable.TH lable="CPU" />
                    <SimpleTable.TH lable="GPU" />
                    <SimpleTable.TH lable="Operating time" />
                    <SimpleTable.TH lable="Status" />
                    <SimpleTable.TH lable="Access" />
                    <SimpleTable.TH lable="" />
                  </SimpleTable.Row>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {filteredNotebooks.map((notebook, i) => {
                    const { spec, id, status } = notebook;
                    return (
                      <SimpleTable.Row
                        key={i}
                        onClick={() =>
                          navigate(AiNotebooksGeneralInformationUrl(id))
                        }
                      >
                        <SimpleTable.TD className="cursor-pointer">
                          <Flex>
                            <HighlighterText
                              searchValue={searchValue}
                              textToHighlight={spec.name}
                              className="max-w-[110px] md:max-w-[400px] lg:max-w-[350px] xl:max-w-[400px] truncate"
                            />
                          </Flex>
                        </SimpleTable.TD>

                        <SimpleTable.TD className="cursor-pointer">
                          <Typography.Text className="!text-xs md:!text-sm">
                            {spec.region}
                          </Typography.Text>
                        </SimpleTable.TD>

                        <SimpleTable.TD className="cursor-pointer">
                          <Typography.Text className="!text-xs">
                            {spec.env.frameworkId} - {spec.env.frameworkVersion}
                          </Typography.Text>
                        </SimpleTable.TD>

                        <SimpleTable.TD className="cursor-pointer">
                          <Typography.Text className="!text-xs">
                            {spec.resources.cpu || "none"}
                          </Typography.Text>
                        </SimpleTable.TD>

                        <SimpleTable.TD className="cursor-pointer">
                          <Typography.Text className="!text-xs">
                            {spec.resources.gpu || "none"}
                          </Typography.Text>
                        </SimpleTable.TD>

                        <SimpleTable.TD className="cursor-pointer">
                          <Typography.Text className="!text-xs ">
                            {formatDuration(status.duration)}
                          </Typography.Text>
                        </SimpleTable.TD>

                        <SimpleTable.TD className="cursor-pointer">
                          <AiNotebookStatusTag state={status.state} />
                        </SimpleTable.TD>

                        <SimpleTable.TD className="cursor-pointer">
                          {status.state === "RUNNING" ? (
                            <Typography.Link
                              href="https://test.com"
                              className="!text-xs md:!text-sm"
                            >
                              {spec.env.editorId}
                            </Typography.Link>
                          ) : (
                            <Typography.Text className="!text-xs md:!text-sm">
                              {spec.env.editorId}
                            </Typography.Text>
                          )}
                        </SimpleTable.TD>

                        <SimpleTable.TD className="w-32 gap-1">
                          <div className="h-full w-full inline-flex items-center justify-center gap-1">
                            <div className="w-8 flex justify-center items-center">
                              <Dropdown
                                anchor="bottom end"
                                itemsClassName="!w-48"
                                menu={[
                                  {
                                    key: "1",
                                    label: (
                                      <div className="flex items-center gap-1">
                                        <DetailsIcon className="w-4" />
                                        Details
                                      </div>
                                    ),
                                    onClick: (_, close) => {
                                      close();
                                      navigate(
                                        AiNotebooksGeneralInformationUrl(id)
                                      );
                                    },
                                    disabled: notebooksLoading,
                                  },
                                  {
                                    key: "2",
                                    label: (
                                      <div className="flex items-center gap-1">
                                        <StartIcon className="w-4" />
                                        Start
                                      </div>
                                    ),
                                    onClick: (_, close) => {
                                      close();
                                      handleStart(id);
                                    },
                                    disabled:
                                      notebooksLoading ||
                                      notebooksActionLoading ||
                                      notebook.status.state === "RUNNING" ||
                                      notebook.status.state === "STARTING" ||
                                      notebook.status.state === "DELETING" ||
                                      notebook.status.state === "RESTARTING" ||
                                      notebook.status.state === "STOPPING",
                                  },
                                  {
                                    key: "3",
                                    label: (
                                      <div className="flex items-center gap-1">
                                        <StopIcon className="w-4" />
                                        Stop
                                      </div>
                                    ),
                                    onClick: (_, close) => {
                                      close();
                                      setStoppingNotebook(id);
                                    },
                                    disabled:
                                      notebooksLoading ||
                                      notebooksActionLoading ||
                                      notebook.status.state === "ERROR" ||
                                      notebook.status.state === "FAILED" ||
                                      notebook.status.state === "STOPPED" ||
                                      notebook.status.state === "STOPPING",
                                  },
                                  {
                                    type: "divider",
                                  },
                                  {
                                    key: "end",
                                    label: (
                                      <div className="flex items-center gap-1">
                                        <ArchiveIcon className="w-4" />
                                        Delete
                                      </div>
                                    ),
                                    danger: true,
                                    onClick: (_, close) => {
                                      close();
                                      setDeleteService(notebook);
                                    },
                                    disabled:
                                      notebooksLoading ||
                                      notebook.status.state === "STARTING" ||
                                      notebook.status.state === "STOPPING" ||
                                      notebook.status.state === "RUNNING",
                                  },
                                ]}
                              >
                                <Button
                                  uiType="icon"
                                  uiSize="small"
                                  className="!px-2"
                                >
                                  <MoreIcon className="w-4 h-4" />
                                </Button>
                              </Dropdown>
                            </div>
                            <ArrowRightIcon className="w-4 h-4 dark:text-slate-100 text-slate-800 cursor-pointer" />
                          </div>
                        </SimpleTable.TD>
                      </SimpleTable.Row>
                    );
                  })}

                  {notebooks.length === 0 && (
                    <SimpleTable.Row
                      withoutHoverStyle={true}
                      className="h-[200px]"
                    >
                      <SimpleTable.TD colSpan={9} className="!border-0">
                        <EmptyState text="You have not created any notebook yet" />
                      </SimpleTable.TD>
                    </SimpleTable.Row>
                  )}
                </SimpleTable.Body>
              </SimpleTable>
            </div>

            <DeleteModal
              title="Delete AI notebook"
              isOpen={deleteService !== null}
              onClose={() => setDeleteService(null)}
              description=""
              confirmButtonText="Confirm"
              confirmButtonType="danger"
              confirmButtonClassName="w-full"
              loading={notebooksActionLoading}
              onConfirm={async () => {
                if (!deleteService) return;

                const action = await dispatch(
                  deleteAiNotebookAsync({ notebookId: deleteService.id })
                );

                if (action.type === "ai-notebooks/delete/fulfilled") {
                  dispatch(getAiNotebooksAsync({}));
                  setDeleteService(null);
                }
              }}
              confirmString={deleteService?.spec.name}
            />
            <QuestionModal
              title="Stop notebook"
              isOpen={stoppingNotebook !== null}
              onConfirm={() => stoppingNotebook && handleStop(stoppingNotebook)}
              onClose={() => {
                setStoppingNotebook(null);
              }}
              loading={notebooksActionLoading}
              description={
                <Flex direction="col" className="gap-3">
                  <Typography.Text className="!text-sm mt-5">
                    When you stop an AI Notebook:
                  </Typography.Text>
                  <ul className="list-disc marker:text-primary-400 pl-8 space-y-1 list-image-[#eee]">
                    <li>
                      <Typography.Text className="!text-xs">
                        Computing resources are freed up (you no longer pay for
                        CPUs and GPUs).
                      </Typography.Text>
                    </li>
                    <li>
                      <Typography.Text className="!text-xs">
                        We back up your workspace (folder/workspace) securely
                        (10 GiB free of charge, then if you exceed this quota,
                        it is charged at the cost of Object Storage)
                      </Typography.Text>
                    </li>
                    <li>
                      {" "}
                      <Typography.Text className="!text-xs">
                        Everything on temporary local storage will be lost.
                      </Typography.Text>
                    </li>
                  </ul>
                  <Typography.Text className="!text-sm">
                    Once it has been stopped, you can either +delete your AI
                    Notebook or +restart it.
                  </Typography.Text>
                  <Typography.Text className="!text-sm">
                    We'll restore your workspace and reallocate computing
                    resources.
                  </Typography.Text>
                </Flex>
              }
              confirmButtonText="Confirm"
              confirmButtonType="primary"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export const AiNotebookStatusTag: React.FC<{
  state: AiNotebookStatus | null;
}> = ({ state }) => {
  return (
    <Tag
      className="text-xs"
      color={
        state === "RUNNING"
          ? "success"
          : state === "ERROR" ||
            state === "FAILED" ||
            state === "SYNC_FAILED" ||
            state === "STOPPED"
          ? "error"
          : state === "DELETING" ||
            state === "RESTARTING" ||
            state === "STARTING" ||
            state === "STOPPING"
          ? "processing"
          : "warning"
      }
    >
      {state}
    </Tag>
  );
};
export default AiNotebooksPage;
